import React, { useState, useEffect } from 'react';
import Modal from '../../../../components/Modal';
import { FeeRegisterRow, FeeOutcome } from '../../config';
import './change-outcome-modal.scss';

interface ChangeOutcomeModalProps {
  isOpen: boolean;
  row: FeeRegisterRow | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (row: FeeRegisterRow, outcome: FeeOutcome) => void;
}

/**
 * Deliberate path for changing an outcome that has already been decided. The
 * register itself only offers one neutral "Change" button on a decided row, so
 * a stray click can't flip a job's status straight from the table.
 */
const ChangeOutcomeModal: React.FC<ChangeOutcomeModalProps> = ({
  isOpen,
  row,
  saving,
  error,
  onClose,
  onConfirm,
}) => {
  const [choice, setChoice] = useState<FeeOutcome | null>(null);

  // Default to the outcome the row doesn't already have — that's the only
  // change worth making from here.
  useEffect(() => {
    if (isOpen && row) {
      setChoice(row.fee_bucket === 'Won' ? 'lost' : 'won');
    }
  }, [isOpen, row]);

  if (!row) return null;

  const jobLabel = `${row.job_number ?? '—'}${row.stage_id != null && row.stage_code ? `.${row.stage_code}` : ''}`;
  const stage = row.workflow_status_name ?? row.project_status;
  const isCurrentlyWon = row.fee_bucket === 'Won';
  const isCurrentlyLost = row.fee_bucket === 'Lost';

  const footer = (
    <div className="modal-footer-actions">
      <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
        Cancel
      </button>
      <button
        type="button"
        className={`btn-primary change-outcome-modal__confirm${choice === 'lost' ? ' is-destructive' : ''}`}
        disabled={saving || !choice}
        onClick={() => choice && onConfirm(row, choice)}
      >
        {saving ? 'Saving…' : choice === 'lost' ? 'Mark Unsuccessful' : 'Mark Won'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Change outcome"
      size="sm"
      hasFooter
      footerContent={footer}
    >
      <div className="change-outcome-modal">
        <div className="change-outcome-modal__job">
          <span className="change-outcome-modal__job-number">{jobLabel}</span>
          <span className="change-outcome-modal__job-name">{row.description}</span>
          {row.stage_id != null && (
            <span className="change-outcome-modal__stage">
              Stage {row.stage_code}{row.stage_description ? ` — ${row.stage_description}` : ''}
            </span>
          )}
        </div>

        <div className="change-outcome-modal__current">
          Currently{' '}
          <span className={`fee-status-badge fee-status-badge--${row.fee_bucket.toLowerCase().replace(/\s+/g, '-')}`}>
            {row.fee_bucket}
          </span>
          {stage && <span className="change-outcome-modal__stage-note">workflow stage: {stage}</span>}
        </div>

        <fieldset className="change-outcome-modal__options">
          <legend>Change to</legend>
          <label
            className={`change-outcome-modal__option${choice === 'won' ? ' is-selected' : ''}${isCurrentlyWon ? ' is-current' : ''}`}
          >
            <input
              type="radio"
              name="feeOutcomeChange"
              value="won"
              checked={choice === 'won'}
              disabled={isCurrentlyWon}
              onChange={() => setChoice('won')}
            />
            <span className="change-outcome-modal__option-body">
              <span className="change-outcome-modal__option-label">
                Won{isCurrentlyWon && <em className="change-outcome-modal__current-tag">current</em>}
              </span>
              <span className="change-outcome-modal__option-hint">
                {isCurrentlyWon
                  ? 'Already won — re-applying would reset the workflow stage.'
                  : 'Moves the job to the first stage of its programme.'}
              </span>
            </span>
          </label>
          <label
            className={`change-outcome-modal__option${choice === 'lost' ? ' is-selected' : ''}${isCurrentlyLost ? ' is-current' : ''}`}
          >
            <input
              type="radio"
              name="feeOutcomeChange"
              value="lost"
              checked={choice === 'lost'}
              disabled={isCurrentlyLost}
              onChange={() => setChoice('lost')}
            />
            <span className="change-outcome-modal__option-body">
              <span className="change-outcome-modal__option-label">
                Lost{isCurrentlyLost && <em className="change-outcome-modal__current-tag">current</em>}
              </span>
              <span className="change-outcome-modal__option-hint">
                {isCurrentlyLost
                  ? 'Already marked unsuccessful.'
                  : 'Marks the job Unsuccessful and removes it from the Budget.'}
              </span>
            </span>
          </label>
        </fieldset>

        {/* The stage isn't stored anywhere before being overwritten, so say so
            plainly rather than letting someone discover it afterwards. */}
        {choice === 'won' && !isCurrentlyWon && (
          <div className="change-outcome-modal__warning">
            Reopening sends the job to the first stage of its programme. Whatever
            stage it held before being marked unsuccessful isn't stored, so it
            can't be restored automatically.
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
      </div>
    </Modal>
  );
};

export default ChangeOutcomeModal;
